/**
 * AppGridEditorTab Component
 *
 * Tab for editing the app launcher grid items.
 * Features drag-drop reordering, visibility toggles, and inline label editing.
 *
 * Part of the AdminEditorModal.
 */

import React, { useMemo } from 'react';
import {
  DndContext,
  DragOverlay,
  DragStartEvent,
  DragEndEvent,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { Eye, EyeOff, RotateCcw, GripVertical } from 'lucide-react';
import { DraggableItem } from '../shared/DraggableItem';
import { InlineEdit } from '../shared/InlineEdit';
import { useAdminEditorStore } from '../../store/adminEditorStore';
import { DEFAULT_APP_GRID_ITEMS } from '../../constants/defaults';
import type { AppGridItem } from '../../types/adminEditor';
import { cn } from '@/shared/utils/cn';

// Dynamic icon import helper - we render icon names as placeholders
// In a real app, you'd use a dynamic icon loader
const IconPlaceholder: React.FC<{ name: string; className?: string }> = ({
  name,
  className = '',
}) => (
  <div
    className={cn(
      'flex items-center justify-center rounded bg-dark-surface/50 text-dark-muted text-[10px]',
      className
    )}
    title={`Icon: ${name}`}
  >
    {name.slice(0, 2).toUpperCase()}
  </div>
);

export interface AppGridEditorTabProps {
  /** Additional CSS classes */
  className?: string;
}

/**
 * AppGridEditorTab - Manage app grid items
 *
 * Features:
 * - Drag-and-drop reorder items
 * - Toggle visibility with eye icon
 * - Inline edit labels
 * - Show icon preview
 * - Reset to defaults button
 */
export const AppGridEditorTab: React.FC<AppGridEditorTabProps> = ({
  className = '',
}) => {
  const [activeId, setActiveId] = React.useState<string | null>(null);

  // Store state and actions
  const appGridItems = useAdminEditorStore((state) => state.appGridItems);
  const setAppGridItems = useAdminEditorStore((state) => state.setAppGridItems);
  const updateAppGridItem = useAdminEditorStore((state) => state.updateAppGridItem);
  const toggleAppGridItemVisibility = useAdminEditorStore(
    (state) => state.toggleAppGridItemVisibility
  );

  // Sort items by order
  const sortedItems = useMemo(
    () => [...appGridItems].sort((a, b) => a.order - b.order),
    [appGridItems]
  );

  // Get the item being dragged
  const activeItem = useMemo(
    () => sortedItems.find((item) => item.id === activeId),
    [sortedItems, activeId]
  );

  // Visible/hidden counts
  const visibleCount = useMemo(
    () => appGridItems.filter((item) => item.visible).length,
    [appGridItems]
  );
  const hiddenCount = appGridItems.length - visibleCount;

  // Configure sensors for both mouse and touch
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) return;

    const oldIndex = sortedItems.findIndex((item) => item.id === active.id);
    const newIndex = sortedItems.findIndex((item) => item.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    // Reorder items
    const newItems = arrayMove(sortedItems, oldIndex, newIndex).map((item, index) => ({
      ...item,
      order: index,
    }));

    setAppGridItems(newItems);
  };

  const handleLabelChange = (id: string, newLabel: string) => {
    updateAppGridItem(id, { label: newLabel });
  };

  const handleResetToDefaults = () => {
    setAppGridItems([...DEFAULT_APP_GRID_ITEMS]);
  };

  return (
    <div className={cn('space-y-4', className)}>
      {/* Header with stats and reset button */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-dark-muted">
          <span className="text-green-400">{visibleCount} visible</span>
          {hiddenCount > 0 && (
            <span className="ml-2 text-dark-muted">{hiddenCount} hidden</span>
          )}
        </div>
        <button
          type="button"
          onClick={handleResetToDefaults}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-dark-surface/50 hover:bg-dark-active rounded transition-all duration-150 text-dark-muted hover:text-dark-text"
          title="Reset to default configuration"
        >
          <RotateCcw size={14} />
          Reset to Defaults
        </button>
      </div>

      {/* Instructions */}
      <p className="text-xs text-dark-muted">
        Drag items to reorder. Click the eye icon to show/hide. Click labels to edit.
      </p>

      {/* Draggable list */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={sortedItems.map((item) => item.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-1 max-h-[400px] overflow-y-auto pr-2">
            {sortedItems.map((item) => (
              <DraggableItem
                key={item.id}
                id={item.id}
                className={cn(
                  'px-2 py-2 bg-dark-surface border border-dark-border/30',
                  !item.visible && 'opacity-50'
                )}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {/* Icon preview */}
                  <IconPlaceholder name={item.icon} className="w-8 h-8 flex-shrink-0" />

                  {/* Label (inline editable) */}
                  <div className="flex-1 min-w-0">
                    <InlineEdit
                      value={item.label}
                      onSave={(newLabel) => handleLabelChange(item.id, newLabel)}
                      maxLength={30}
                      className="text-sm"
                      textClassName={cn(!item.visible && 'text-dark-muted')}
                    />
                    <p className="text-xs text-dark-muted truncate mt-0.5">
                      {item.route}
                    </p>
                  </div>

                  {/* Visibility toggle */}
                  <button
                    type="button"
                    onClick={() => toggleAppGridItemVisibility(item.id)}
                    className={cn(
                      'p-1.5 rounded transition-all duration-150 flex-shrink-0',
                      item.visible
                        ? 'text-green-400 hover:text-green-300 hover:bg-green-500/10'
                        : 'text-dark-muted hover:text-dark-text hover:bg-dark-surface/50'
                    )}
                    title={item.visible ? 'Hide this item' : 'Show this item'}
                  >
                    {item.visible ? <Eye size={16} /> : <EyeOff size={16} />}
                  </button>
                </div>
              </DraggableItem>
            ))}
          </div>
        </SortableContext>

        {/* Drag Overlay */}
        <DragOverlay>
          {activeItem && (
            <div className="flex items-center gap-3 px-3 py-2 bg-gradient-to-br from-dark-surface/95 to-dark-bg/85 backdrop-blur-sm border border-brand-500 rounded shadow-lg">
              <GripVertical size={16} className="text-dark-muted" />
              <IconPlaceholder name={activeItem.icon} className="w-8 h-8" />
              <span className="text-sm text-dark-text">{activeItem.label}</span>
            </div>
          )}
        </DragOverlay>
      </DndContext>

      {/* Footer info */}
      <div className="pt-2 border-t border-dark-border/30">
        <p className="text-xs text-dark-muted text-center">
          {sortedItems.length} total items
        </p>
      </div>
    </div>
  );
};

export default AppGridEditorTab;
